import React, { useEffect, useState, useMemo, useRef } from 'react';
import './GradualSpacingText.css';

const GradualSpacingText = ({ text = '', className = '', spacing, playKey, stagger = 20, playOnVisible = true }) => {
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);
  // split into tokens so we can preserve spaces and keep words unbroken
  const tokens = useMemo(() => text.split(/(\s+)/), [text]);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Play when text or playKey changes - only auto-play if the element is already visible
  useEffect(() => {
    // If the caller disabled playOnVisible, behave as before and play immediately
    if (!playOnVisible) {
      let t = setTimeout(() => setVisible(true), 20);
      setVisible(false);
      return () => clearTimeout(t);
    }

    const el = rootRef.current;
    // If the element is already in the viewport, trigger the animation immediately
    if (el) {
      const rect = el.getBoundingClientRect();
      const inView = rect.top < (window.innerHeight || 0) && rect.bottom > 0;
      if (inView) {
        let t = setTimeout(() => setVisible(true), 20);
        setVisible(false);
        return () => clearTimeout(t);
      }
    }

    // Otherwise, ensure it's hidden and let IntersectionObserver / ancestor observer trigger it later
    setVisible(false);

    // no cleanup necessary
    return () => {};
  }, [text, playKey, playOnVisible]);

  // Optionally trigger on scroll / parent.reveal becoming visible
  useEffect(() => {
    if (!playOnVisible || typeof window === 'undefined') return;

    const el = rootRef.current;
    if (!el) return;

    // If any ancestor already has .is-visible, show immediately
    const hasVisibleAncestor = () => {
      let node = el.parentElement;
      while (node) {
        if (node.classList && node.classList.contains('is-visible')) return true;
        node = node.parentElement;
      }
      return false;
    };

    if (hasVisibleAncestor()) {
      setVisible(true);
      return;
    }

    // Observe nearest .reveal ancestor for intersection (more reliable than mutation-only)
    let revealAncestor = el.closest('.reveal');
    let mutationObserver = null;
    let ancestorIo = null;

    if (revealAncestor) {
      // If it's already visible, show immediately
      if (revealAncestor.classList.contains('is-visible')) {
        setVisible(true);
      } else {
        try {
          ancestorIo = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setVisible(true);
                if (ancestorIo) ancestorIo.disconnect();
              }
            });
          }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
          ancestorIo.observe(revealAncestor);
        } catch (e) {
          // IntersectionObserver failed - fall back to mutation observer
          mutationObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
              if (m.type === 'attributes' && m.attributeName === 'class') {
                if (revealAncestor.classList.contains('is-visible')) {
                  setVisible(true);
                  if (mutationObserver) mutationObserver.disconnect();
                  break;
                }
              }
            }
          });
          mutationObserver.observe(revealAncestor, { attributes: true, attributeFilter: ['class'] });
        }
      }
    }

    // Also use IntersectionObserver on self as a fallback
    let io = null;
    try {
      io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (io) io.disconnect();
            if (ancestorIo) ancestorIo.disconnect();
            if (mutationObserver) mutationObserver.disconnect();
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

      io.observe(el);
    } catch (e) {
      // IntersectionObserver may not be available - fall back to immediate show
      setVisible(true);
    }
    return () => {
      if (mutationObserver) mutationObserver.disconnect();
      if (io) io.disconnect();
    };
  }, [playOnVisible]);

  // track a running index so stagger continues across words
  let charCount = 0;

  return (
    <span
      ref={rootRef}
      className={`gradual-spacing ${className} ${visible ? 'is-visible' : ''}`}
      aria-hidden="true"
      style={spacing ? { ['--gs-spacing']: spacing } : undefined}
    >
      {tokens.map((token, ti) => {
        // whitespace tokens: render as plain text so browser can wrap at spaces
        if (/\s+/.test(token)) {
          return token;
        }

        const wordChars = Array.from(token);
        return (
          <span key={`word-${ti}`} className="gradual-word">
            {wordChars.map((ch, i) => {
              const idx = charCount++;
              return (
                <span
                  key={`${ti}-${i}`}
                  className="gradual-char"
                  style={{ 
                    animationDelay: `${idx * stagger}ms`, 
                    transitionDelay: `${idx * stagger}ms`,
                    '--i': i 
                  }}
                >
                  {ch}
                </span>
              );
            })}
          </span>
        );
      })}

      <span className="sr-only">{text}</span>
    </span>
  );
};

export default GradualSpacingText;
